import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, CheckCircle2, XCircle, MessageSquare, Paperclip, Gavel, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDateTime, fmtDate, fmtMoney } from "@/lib/format";
import { downloadFile } from "@/lib/enquiries";

const money = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN")}`);
const STEP_BADGE = {
  locked: "bg-slate-100 text-slate-500 border-slate-200",
  pending: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  opened: "bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]",
  approved: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
  rejected: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function ApproverApprovalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/approver/approvals/${id}`)
      .then((r) => { setData(r.data); setError(""); })
      .catch((e) => setError(formatApiError(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="mx-auto max-w-lg space-y-4 py-16 text-center" data-testid="approval-error">
        <XCircle className="mx-auto h-10 w-10 text-[#EF4444]" />
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={() => navigate("/approver/dashboard")}><ArrowLeft className="mr-1.5 h-4 w-4" /> Back to Dashboard</Button>
      </div>
    );
  }
  if (!data) return <div className="flex h-64 items-center justify-center" data-testid="approval-detail-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const { step, chain, steps, enquiry, buyer_org_name, auction, documents } = data;
  const snap = chain.snapshot || [];
  const done = step.status === "approved" || step.status === "rejected";
  const chainOpen = chain.status === "in_progress";
  const lowestQuoted = Math.min(...snap.filter((s) => s.quoted_price != null).map((s) => s.quoted_price), Infinity);
  const lowestBase = Math.min(...snap.filter((s) => s.base_value != null).map((s) => s.base_value), Infinity);
  const lowestFinal = Math.min(...snap.filter((s) => (s.final_value ?? s.total_price) != null).map((s) => s.final_value ?? s.total_price), Infinity);
  const fastestDelivery = Math.min(...snap.filter((s) => s.delivery_days != null).map((s) => s.delivery_days), Infinity);
  const recommended = snap.find((s) => s.supplier_company_id === chain.recommended_seller_id);

  const decide = async () => {
    if (confirm === "reject" && !comment.trim()) {
      toast.error("A comment is required when rejecting");
      setConfirm(null);
      return;
    }
    setSaving(true);
    try {
      await api.post(`/approver/approvals/${id}/${confirm === "approve" ? "approve" : "reject"}`, { comment });
      toast.success(confirm === "approve" ? "Approved" : "Rejected");
      setConfirm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="approver-approval-detail" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="icon" data-testid="approval-back-btn" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-heading text-2xl font-semibold tracking-tight font-mono" data-testid="approval-enquiry-number">{enquiry.enquiry_number}</h1>
            <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${chain.status === "approved" ? STEP_BADGE.approved : chain.status === "rejected" ? STEP_BADGE.rejected : STEP_BADGE.pending}`} data-testid="approval-chain-status">
              {chain.status.replace(/_/g, " ")}
            </span>
            <span className="text-xs text-muted-foreground">Step {step.sequence_number} of {steps.length}</span>
          </div>
          <p className="text-sm text-muted-foreground">{buyer_org_name}</p>
        </div>
        <Button variant="outline" data-testid="download-comparative-btn" onClick={() => downloadFile(`/approver/approvals/${id}/pdf`, `comparative-${enquiry.enquiry_number}.pdf`)}>
          <Download className="mr-1.5 h-4 w-4" /> Comparative Statement
        </Button>
        <Button variant="outline" data-testid="download-enquiry-btn" onClick={() => downloadFile(`/enquiries/${enquiry.id}/download`, `${enquiry.enquiry_number}.pdf`)}>
          <Download className="mr-1.5 h-4 w-4" /> Buyer Enquiry
        </Button>
      </div>

      {chain.overdue && <p className="rounded-md border border-[#FECACA] bg-[#FEE2E2]/40 px-4 py-2.5 text-sm font-medium text-[#991B1B]" data-testid="approval-overdue-banner">This approval is OVERDUE (deadline {fmtDateTime(chain.deadline)}).</p>}

      <div className="rounded-lg border bg-card">
        <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Event Overview</h2>
        <div className="overflow-x-auto px-5 pt-4">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-2 pr-4 font-medium">#</th><th className="py-2 pr-4 font-medium">Material Code</th><th className="py-2 pr-4 font-medium">Description</th><th className="py-2 pr-4 font-medium text-right">Qty</th><th className="py-2 font-medium">UOM</th>
            </tr></thead>
            <tbody>
              {enquiry.items.map((it, i) => (
                <tr key={it.id || it.material_code} className="border-b last:border-0">
                  <td className="py-2.5 pr-4 font-mono text-xs text-muted-foreground">{i + 1}</td>
                  <td className="py-2.5 pr-4 font-mono text-xs font-semibold">{it.material_code}</td>
                  <td className="py-2.5 pr-4">{it.description}</td>
                  <td className="py-2.5 pr-4 text-right font-mono">{it.quantity}</td>
                  <td className="py-2.5 font-mono text-xs">{it.uom}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 px-5 py-4 md:grid-cols-4">
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Event Date</p><p className="mt-1 text-sm">{fmtDate(enquiry.created_at)}</p></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Event Deadline</p><p className="mt-1 text-sm">{fmtDateTime(enquiry.deadline)}</p></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Sellers</p><p className="mt-1 font-mono text-sm">{snap.length}</p></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Floated By</p><p className="mt-1 text-sm">{enquiry.created_by}</p></div>
        </div>
      </div>

      {chain.buyer_comment && (
        <div className="rounded-lg border border-[#BFDBFE] bg-[#DBEAFE]/30 px-5 py-4" data-testid="approval-buyer-comment">
          <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-[#1E40AF]"><MessageSquare className="h-3.5 w-3.5" /> Buyer Comment — {chain.created_by}</p>
          <p className="mt-1.5 text-sm">{chain.buyer_comment}</p>
        </div>
      )}
      {recommended && (
        <div className="rounded-lg border border-[#BBF7D0] bg-[#DCFCE7]/30 px-5 py-4" data-testid="approval-recommended-seller">
          <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-[#166534]"><Trophy className="h-3.5 w-3.5" /> Recommended Seller</p>
          <p className="mt-1.5 text-sm font-medium">{recommended.supplier_name}{chain.recommended_reason && <span className="font-normal text-muted-foreground"> — {chain.recommended_reason}</span>}</p>
        </div>
      )}

      <div className="rounded-lg border bg-card">
        <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Price Comparative Statement</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="price-comparative-table">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-5 py-2.5 font-medium">Seller</th>
                <th className="px-3 py-2.5 font-medium">Technical</th>
                <th className="px-3 py-2.5 font-medium text-right">Quoted Price</th>
                <th className="px-3 py-2.5 font-medium text-right">Total Base Value</th>
                <th className="px-3 py-2.5 font-medium text-right">GST %</th>
                <th className="px-3 py-2.5 font-medium text-right">GST Amount</th>
                <th className="px-3 py-2.5 font-medium text-right">Total Final Value</th>
                <th className="px-3 py-2.5 font-medium text-right">Delivery</th>
                <th className="px-3 py-2.5 font-medium">Payment Terms</th>
                <th className="px-3 py-2.5 font-medium">Incoterms</th>
                <th className="px-3 py-2.5 font-medium text-right">Auction Final</th>
                <th className="px-3 py-2.5 font-medium">Rank</th>
                <th className="px-3 py-2.5 font-medium text-right">Offer</th>
              </tr>
            </thead>
            <tbody>
              {snap.map((s) => (
                <tr key={s.invite_id} data-testid={`pcs-row-${s.supplier_name.replace(/\W+/g, "-").toLowerCase()}`}
                  className={`border-b last:border-0 ${(s.final_value ?? s.total_price) != null && (s.final_value ?? s.total_price) === lowestFinal ? "bg-[#DCFCE7]/20" : ""}`}>
                  <td className="px-5 py-3 font-medium">
                    {s.supplier_name}
                    {s.supplier_company_id === chain.recommended_seller_id && <Trophy className="ml-1.5 inline h-3.5 w-3.5 text-amber-500" />}
                  </td>
                  <td className="px-3 py-3">
                    {s.technical
                      ? <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${s.technical.status === "approved" ? STEP_BADGE.approved : s.technical.status === "rejected" ? STEP_BADGE.rejected : STEP_BADGE.pending}`}>{s.technical.status}</span>
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-xs font-semibold">{fmtMoney(s.quoted_price)}</td>
                  <td className="px-3 py-3 text-right font-mono text-xs">
                    {fmtMoney(s.base_value ?? s.quoted_price)}
                    {s.base_value != null && s.base_value === lowestBase && <span className="ml-1 rounded bg-[#DCFCE7] px-1 text-[9px] font-bold text-[#166534]">LOWEST BASE</span>}
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-xs">{s.gst_pct}%</td>
                  <td className="px-3 py-3 text-right font-mono text-xs">{fmtMoney(s.gst_amount)}</td>
                  <td className="px-3 py-3 text-right font-mono text-xs font-semibold">
                    {fmtMoney(s.final_value ?? s.total_price)}
                    {(s.final_value ?? s.total_price) === lowestFinal && <span className="ml-1 rounded bg-[#DCFCE7] px-1 text-[9px] font-bold text-[#166534]" data-testid={`pcs-lowest-final-${s.invite_id.slice(0, 8)}`}>LOWEST FINAL</span>}
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-xs">
                    {s.delivery_days}d
                    {s.delivery_days === fastestDelivery && <span className="ml-1 rounded bg-[#DBEAFE] px-1 text-[9px] font-bold text-[#1E40AF]">FASTEST</span>}
                  </td>
                  <td className="px-3 py-3 text-xs">{s.payment_terms}</td>
                  <td className="px-3 py-3 font-mono text-xs">{s.incoterms}</td>
                  <td className="px-3 py-3 text-right font-mono text-xs">{s.auction?.final_price != null ? money(s.auction.final_price) : "—"}</td>
                  <td className="px-3 py-3 font-mono text-xs">{s.auction?.rank ? `#${s.auction.rank}` : "—"}</td>
                  <td className="px-3 py-3 text-right">
                    <button className="text-xs text-blue-700 hover:underline" data-testid={`pcs-offer-${s.invite_id.slice(0, 8)}`}
                      onClick={() => downloadFile(`/approver/approvals/${id}/offers/${s.invite_id}/pdf`, `offer-${s.supplier_name.replace(/\s+/g, "_")}.pdf`)}>
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-5 border-t px-5 py-3" data-testid="pcs-highlights">
          <span className="text-xs uppercase tracking-wide">Lowest Base Value: <span className="font-mono font-semibold" data-testid="highlight-lowest-base">{lowestBase !== Infinity ? fmtMoney(lowestBase) : "—"}</span></span>
          <span className="text-xs uppercase tracking-wide">Lowest Final Value: <span className="font-mono font-semibold" data-testid="highlight-lowest-final">{lowestFinal !== Infinity ? fmtMoney(lowestFinal) : "—"}</span></span>
          <span className="text-xs uppercase tracking-wide">Fastest Delivery: <span className="font-mono font-semibold" data-testid="highlight-fastest">{fastestDelivery !== Infinity ? `${fastestDelivery} days` : "—"}</span></span>
          <span className="text-[10px] text-muted-foreground">Informational highlights only — not an automatic supplier recommendation.</span>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border bg-card">
          <h3 className="border-b px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Delivery Comparison</h3>
          <div className="divide-y" data-testid="delivery-comparison">
            {snap.map((s) => (
              <div key={s.invite_id} className="flex justify-between px-4 py-2 text-sm"><span className="text-xs">{s.supplier_name}</span><span className="font-mono text-xs">{s.delivery_days} days (vs {enquiry.delivery_days}d asked)</span></div>
            ))}
          </div>
        </div>
        <div className="rounded-lg border bg-card">
          <h3 className="border-b px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Payment Terms</h3>
          <div className="divide-y" data-testid="payment-comparison">
            {snap.map((s) => (
              <div key={s.invite_id} className="flex justify-between px-4 py-2 text-sm"><span className="text-xs">{s.supplier_name}</span><span className="text-xs">{s.payment_terms}</span></div>
            ))}
          </div>
        </div>
        <div className="rounded-lg border bg-card">
          <h3 className="border-b px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Incoterms</h3>
          <div className="divide-y" data-testid="incoterms-comparison">
            {snap.map((s) => (
              <div key={s.invite_id} className="flex justify-between px-4 py-2 text-sm"><span className="text-xs">{s.supplier_name}</span><span className="font-mono text-xs">{s.incoterms}</span></div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Technical Evaluation</h2>
        <div className="divide-y" data-testid="technical-evaluation-section">
          {snap.filter((s) => s.technical).length === 0 && <p className="px-5 py-4 text-sm text-muted-foreground">No technical reviews were conducted for this event.</p>}
          {snap.filter((s) => s.technical).map((s) => (
            <div key={s.invite_id} className="px-5 py-3" data-testid={`tech-review-${s.supplier_name.replace(/\W+/g, "-").toLowerCase()}`}>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium">{s.supplier_name}</p>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${s.technical.status === "approved" ? STEP_BADGE.approved : STEP_BADGE.rejected}`}>{s.technical.status}</span>
                <span className="text-xs text-muted-foreground">{s.technical.reviewer}{s.technical.reviewed_at ? ` · ${fmtDateTime(s.technical.reviewed_at)}` : ""}</span>
              </div>
              {s.technical.comment && <p className="mt-1 text-xs text-muted-foreground">"{s.technical.comment}"</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <h2 className="border-b px-5 py-3 font-heading text-base font-semibold flex items-center gap-2"><Gavel className="h-4 w-4 text-slate-500" /> Auction Results</h2>
        {auction ? (
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 px-5 py-4 md:grid-cols-4" data-testid="auction-results-section">
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Type</p><p className="mt-1 text-sm capitalize">Reverse {auction.type}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p><p className="mt-1 text-sm capitalize">{auction.status.replace(/_/g, " ")}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Starting Price</p><p className="mt-1 font-mono text-sm">{money(auction.starting_price)}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Final Price</p><p className="mt-1 font-mono text-sm font-semibold">{money(auction.final_price)}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Participants</p><p className="mt-1 font-mono text-sm">{auction.participants}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Bids / Actions</p><p className="mt-1 font-mono text-sm">{auction.bids}</p></div>
            {auction.savings != null && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Savings</p><p className="mt-1 font-mono text-sm font-semibold text-[#166534]">{money(auction.savings)}</p></div>}
          </div>
        ) : (
          <p className="px-5 py-4 text-sm text-muted-foreground" data-testid="no-auction-note">Auction was not conducted for this event.</p>
        )}
      </div>

      {documents.length > 0 && (
        <div className="rounded-lg border bg-card">
          <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Documents</h2>
          <div className="flex flex-wrap items-center gap-2 px-5 py-3" data-testid="approval-documents">
            {documents.map((f) => (
              <span key={f.id} className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs">
                <Paperclip className="h-3 w-3" /> {f.filename}
                <span className="rounded bg-slate-100 px-1 text-[10px] uppercase text-slate-500">{f.category === "approval_document" ? "APPROVAL_DOCUMENT" : f.category || "other"}</span>
                <button data-testid={`doc-view-${f.id.slice(0, 8)}`} className="ml-1 text-blue-700 hover:underline"
                  onClick={async () => { try { const r = await api.get(`/enquiry-files/${f.id}/download`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch { toast.error("View failed — access denied"); } }}>View</button>
                <button data-testid={`doc-download-${f.id.slice(0, 8)}`} className="ml-1 text-blue-700 hover:underline"
                  onClick={() => downloadFile(`/enquiry-files/${f.id}/download`, f.filename)}>Download</button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border bg-card">
        <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Approval Chain</h2>
        <div className="divide-y" data-testid="approval-chain-view">
          {steps.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`chain-view-step-${s.sequence_number}`}>
              <span className="font-mono text-xs text-muted-foreground">{s.sequence_number}.</span>
              <span className="text-sm font-medium">{s.approver_name}</span>
              <span className="text-xs text-muted-foreground">{s.approver_designation}</span>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${STEP_BADGE[s.status] || STEP_BADGE.pending}`}>{s.status}</span>
              {s.comment && <span className="text-xs text-muted-foreground">"{s.comment}"</span>}
              {s.decided_at && <span className="ml-auto text-xs text-muted-foreground">{fmtDateTime(s.decided_at)}</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border bg-card" data-testid="approval-decision-card">
        <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Approval Decision</h2>
        {done ? (
          <div className="px-5 py-4" data-testid="approval-result">
            <div className="flex items-center gap-2">
              {step.status === "approved"
                ? <><CheckCircle2 className="h-5 w-5 text-[#166534]" /><span className="font-semibold text-[#166534]">YOU APPROVED</span></>
                : <><XCircle className="h-5 w-5 text-[#991B1B]" /><span className="font-semibold text-[#991B1B]">YOU REJECTED</span></>}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Decided {fmtDateTime(step.decided_at)}</p>
            {step.comment && <p className="mt-2 rounded-md border bg-slate-50 p-3 text-sm">"{step.comment}"</p>}
          </div>
        ) : !chainOpen ? (
          <p className="px-5 py-4 text-sm text-muted-foreground" data-testid="chain-not-open">The approval chain is {chain.status.replace(/_/g, " ")} — no decisions can be made.</p>
        ) : (
          <div className="space-y-4 px-5 py-4">
            <div>
              <Label>Comment (optional when approving, required when rejecting)</Label>
              <Textarea data-testid="approver-comment-input" rows={3} className="mt-1.5" placeholder="e.g. Commercial price is above the approved budget. Please renegotiate…" value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>
            <div className="flex gap-3">
              <Button data-testid="approve-event-btn" onClick={() => setConfirm("approve")} className="bg-[#166534] hover:bg-[#14532D]">
                <CheckCircle2 className="mr-1.5 h-4 w-4" /> Approve
              </Button>
              <Button data-testid="reject-event-btn" variant="outline" onClick={() => setConfirm("reject")} className="border-red-200 text-red-700 hover:bg-red-50">
                <XCircle className="mr-1.5 h-4 w-4" /> Reject
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
        <DialogContent className="bg-white" data-testid="approval-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{confirm === "approve" ? "Approve Event" : "Reject Event"}</DialogTitle>
            <DialogDescription>
              {confirm === "approve"
                ? "Are you sure you want to approve this event?"
                : "Are you sure you want to reject this event? The entire approval chain will stop."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="approval-cancel-btn" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button data-testid="approval-confirm-submit" onClick={decide} disabled={saving}
              className={confirm === "approve" ? "bg-[#166534] hover:bg-[#14532D]" : "bg-red-700 hover:bg-red-800"}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {confirm === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
